import React, { useEffect, useState } from "react";

function CustomPINDisplay({
  value = "",
  onChange,
  OTPLength = 4,
  otpType = "number",
  disabled = false,
  className = "",
  inputClassName = "",
  autoFocus = false,
  onKeyDown,
}) {
  const [pin, setPin] = useState(value || "");

  useEffect(() => {
    if (value !== undefined) {
      setPin(value || "");
    }
  }, [value]);

  const isReadonly = inputClassName.includes("readonly-input");
  const isDisplayOnly = isReadonly || disabled;

  // For display-only mode, just show the PIN digits in styled boxes
  if (isDisplayOnly) {
    const pinDigits = pin.toString().split("").slice(0, OTPLength);
    
    return (
      <div className={className} style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
        {Array.from({ length: OTPLength }).map((_, index) => (
          <div
            key={index}
            className={inputClassName}
            style={{
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              fontSize: "18px",
              fontWeight: 800,
              border: "1px solid grey",
              borderRadius: "5px",
              backgroundColor: "#f7f7f7",
              userSelect: "none",
            }}
          >
            {pinDigits[index] || ""}
          </div>
        ))}
      </div>
    );
  }

  // For interactive mode (modal), keep the input functionality
  const inputRefs = React.useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, newValue) => {
    if (disabled) return;

    if (otpType === "number" && !/^\d*$/.test(newValue)) {
      return;
    }

    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }

    const newPin = pin.split("");
    newPin[index] = newValue;
    const updatedPin = newPin.join("").slice(0, OTPLength);
    setPin(updatedPin);

    if (onChange) {
      onChange(updatedPin);
    }

    if (newValue && index < OTPLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    if (disabled) return;

    if (e.key === "Backspace") {
      if (!pin[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newPin = pin.split("");
        newPin[index] = "";
        const updatedPin = newPin.join("");
        setPin(updatedPin);
        if (onChange) {
          onChange(updatedPin);
        }
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTPLength - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Delete") {
      const newPin = pin.split("");
      newPin[index] = "";
      const updatedPin = newPin.join("");
      setPin(updatedPin);
      if (onChange) {
        onChange(updatedPin);
      }
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, OTPLength);
    
    if (otpType === "number" && !/^\d*$/.test(pastedData)) {
      return;
    }

    setPin(pastedData);
    if (onChange) {
      onChange(pastedData);
    }

    const focusIndex = Math.min(pastedData.length, OTPLength - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className={className} style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
      {Array.from({ length: OTPLength }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type={otpType === "number" ? "tel" : "text"}
          value={pin[index] || ""}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={index === 0 ? handlePaste : undefined}
          disabled={disabled}
          className={inputClassName}
          style={{
            width: "48px",
            height: "48px",
            textAlign: "center",
            fontSize: "18px",
            fontWeight: 800,
            border: "1px solid grey",
            borderRadius: "5px",
            outline: "none",
          }}
          maxLength={1}
          inputMode={otpType === "number" ? "numeric" : "text"}
        />
      ))}
    </div>
  );
}

export default CustomPINDisplay;

